import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import KeywordsFilters from './KeywordsFilters';
import KeywordsTable from './KeywordsTable';
import { apiService } from '@/services/api';

interface Category {
  category_id: number;
  category_name: string;
  count: number;
}

interface KeywordsContainerProps {
  searchId: string | number;
  keywords: any[];
  isLoadingKeywords: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  categories: Category[];
  setCurrentPage: (page: number) => void;
  onAnalyzeSelected?: (selectedIds: string[]) => void;
  isAnalyzing?: boolean;
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  onSelectedKeywordsChange?: (selectedKeywords: any[]) => void;
}

const KeywordsContainer: React.FC<KeywordsContainerProps> = ({
  searchId,
  keywords,
  isLoadingKeywords,
  totalCount,
  currentPage,
  totalPages,
  categories,
  setCurrentPage,
  onAnalyzeSelected,
  isAnalyzing = false,
  searchTerm: externalSearchTerm,
  setSearchTerm: externalSetSearchTerm,
  onSelectedKeywordsChange,
}) => {
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  
  // Use external search term if provided, otherwise use internal
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm = externalSetSearchTerm || setInternalSearchTerm;
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all');
  const [volumeFilter, setVolumeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [volumeValue, setVolumeValue] = useState<number>(5000000);
  const [kdValue, setKdValue] = useState<number>(100);
  const [localKeywords, setLocalKeywords] = useState<any[]>([]);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [localTotalCount, setLocalTotalCount] = useState(0);
  const [localTotalPages, setLocalTotalPages] = useState(1);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedKeywords(new Set(keywords.map(k => k.id.toString())));
    } else {
      setSelectedKeywords(new Set());
    }
  }, [keywords]);

  const handleSelectKeyword = useCallback((keywordId: string, checked: boolean) => {
    const newSelected = new Set(selectedKeywords);
    if (checked) {
      newSelected.add(keywordId);
    } else {
      newSelected.delete(keywordId);
    }
    console.log("newSelected", newSelected);
    setSelectedKeywords(newSelected);
  }, [selectedKeywords]);

  const handleAnalyzeSelected = useCallback(() => {
    console.log("selectedKeywords", selectedKeywords);
    if (onAnalyzeSelected && selectedKeywords.size > 0) {
      onAnalyzeSelected(Array.from(selectedKeywords));
    }
  }, [onAnalyzeSelected, selectedKeywords]);

  // const handleVolumeChange = useCallback((value: number[]) => {
  //   setVolumeValue(value[0]);
  // }, []);

  // API call to fetch keywords with filters
  const fetchKeywordsWithFilters = useCallback(async () => {
    if (!searchId) return;
    
    setIsLoadingLocal(true);
    try {
      const response = await apiService.get(`/search/${searchId}/keywords/`, {
        params: {
          page: currentPage,
          page_size: 20,
          search: searchTerm || undefined,
          competition: difficultyFilter !== 'all' ? difficultyFilter.toUpperCase() : undefined,
          volume_max: volumeValue < 5000000 ? volumeValue : undefined,
          da_score_max: kdValue < 100 ? kdValue : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
        },
      });
      
      const data = response.data;
      setLocalKeywords(data.keywords || []);
      setLocalTotalCount(data.total_keywords || 0);
      setLocalTotalPages(data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching keywords with filters:', error);
      setLocalKeywords([]);
      setLocalTotalCount(0);
      setLocalTotalPages(1);
    } finally {
      setIsLoadingLocal(false);
    }
  }, [searchId, currentPage, searchTerm, difficultyFilter, categoryFilter, volumeValue, kdValue]);

  // Use local keywords if available, otherwise fall back to props
  const displayKeywords = localKeywords.length > 0 ? localKeywords : keywords;
  const displayTotalCount = localTotalCount > 0 ? localTotalCount : totalCount;
  const displayTotalPages = localTotalPages > 1 ? localTotalPages : totalPages;
  const displayIsLoading = isLoadingLocal || isLoadingKeywords;

  // Notify parent when selected keywords change
  useEffect(() => {
    if (onSelectedKeywordsChange) {
      const selectedKeywordObjects = displayKeywords.filter(keyword => 
        selectedKeywords.has(keyword.id.toString())
      );
      onSelectedKeywordsChange(selectedKeywordObjects);
    }
  }, [selectedKeywords, displayKeywords, onSelectedKeywordsChange]);

  // Effect to trigger API call when filters change
  useEffect(() => {
    // Only make API call if we have a searchId and at least one filter is active
    const hasActiveFilters = searchTerm || difficultyFilter !== 'all' || volumeFilter !== 'all' || categoryFilter !== 'all' || volumeValue < 5000000 || kdValue < 100;
    
    if (searchId && hasActiveFilters) {
      fetchKeywordsWithFilters();
    } else if (searchId) {
      // Reset local state when no filters are active
      setLocalKeywords([]);
      setLocalTotalCount(0);
      setLocalTotalPages(1);
    }
  }, [searchId, currentPage, searchTerm, difficultyFilter, volumeFilter, categoryFilter, volumeValue, kdValue, fetchKeywordsWithFilters]);

  return (
    <Card className="px-0 w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          
          
          <div className="text-sm text-muted-foreground flex">
            <BarChart3 className="mr-0 h-4 w-4 mr-1" /> <>{totalCount} Results</>
          </div>

        </div>
      </CardHeader>
      <CardContent>
        {/* Filters Component */}
        <KeywordsFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          difficultyFilter={difficultyFilter}
          setDifficultyFilter={setDifficultyFilter}
          volumeFilter={volumeFilter}
          setVolumeFilter={setVolumeFilter}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          categories={categories}
          totalCount={totalCount}
          selectedKeywordsCount={selectedKeywords.size}
          onAnalyzeSelected={handleAnalyzeSelected}
          isAnalyzing={isAnalyzing}
          volumeValue={volumeValue}
          setVolumeValue={setVolumeValue}
          kdValue={kdValue}
          setKdValue={setKdValue}
        />

        {/* Table Component */}
        <KeywordsTable
          keywords={displayKeywords}
          isLoadingKeywords={displayIsLoading}
          totalCount={displayTotalCount}
          currentPage={currentPage}
          totalPages={displayTotalPages}
          selectedKeywords={selectedKeywords}
          onSelectAll={handleSelectAll}
          onSelectKeyword={handleSelectKeyword}
          setCurrentPage={setCurrentPage}
        />
      </CardContent>
    </Card>
  );
};

export default KeywordsContainer; 